import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { format } from 'date-fns'
import { Check, GripVertical, History, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  archiveTemplate,
  createTemplate,
  getExerciseLatestStats,
  getExercises,
  getProfile,
  getTemplates,
  proposeTemplates,
  updateTemplate,
} from '@/api/services/workout'
import type {
  AiTemplateProposal,
  Exercise,
  ExerciseLatestStats,
  ProfileRead,
  TemplateItemInput,
  WorkoutTemplate,
} from '@/api/types/workout'
import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'

type Props = { onClose: () => void; onOpenProfile: () => void }

type FormItem = TemplateItemInput & { _uid: string }

type FormState = {
  name: string
  notes: string
  items: FormItem[]
}

const EMPTY_FORM: FormState = { name: '', notes: '', items: [] }

// セット数・レップ数は空欄にできるようにしてあり、未入力のまま保存されたらこの値を使う
const DEFAULT_TARGET_SETS = 3
const DEFAULT_TARGET_REPS = 8

const isProfileIncomplete = (p: ProfileRead): boolean => {
  return p.training_days_per_week == null || p.session_duration_minutes == null || p.available_equipments.length === 0
}

type LatestStatsMap = Map<number, ExerciseLatestStats>

type ItemValues = Pick<TemplateItemInput, 'target_sets' | 'target_reps' | 'target_weight_kg' | 'rest_seconds'>

// 休憩は AI の次回プランを優先し、無ければ種目の既定値にする
const valuesFromLatest = (stats: ExerciseLatestStats, exercise: Exercise | undefined): ItemValues => ({
  target_sets: stats.sets,
  target_reps: stats.reps ?? undefined,
  target_weight_kg: stats.weight_kg,
  rest_seconds: stats.rest_seconds ?? exercise?.default_rest_seconds ?? null,
})

export const TemplatesManager = ({ onClose, onOpenProfile }: Props) => {
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([])
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [latestStats, setLatestStats] = useState<LatestStatsMap>(() => new Map())
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<WorkoutTemplate | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [open, setOpen] = useState(false)
  const uidRef = useRef(0)
  const nextUid = () => `item-${++uidRef.current}`

  // AI提案
  const [aiOpen, setAiOpen] = useState(false)
  const [aiGoal, setAiGoal] = useState('リーンバルク')
  const [aiRequest, setAiRequest] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiProposals, setAiProposals] = useState<AiTemplateProposal[]>([])
  const [profileGuideOpen, setProfileGuideOpen] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      // 直近の記録は初期値に使うだけなので、取れなくてもテンプレート管理は使えるようにする
      const [t, e, stats] = await Promise.all([
        getTemplates(false),
        getExercises(false),
        getExerciseLatestStats().catch((err: unknown) => {
          console.warn('直近の記録の取得に失敗', err)
          return []
        }),
      ])
      setTemplates(t)
      setExercises(e)
      setLatestStats(new Map(stats.map((s) => [s.exercise_id, s])))
    } catch (err) {
      console.error(err)
      toast.error('データ取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const openNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setOpen(true)
  }

  const openEdit = (t: WorkoutTemplate) => {
    setEditing(t)
    setForm({
      name: t.name,
      notes: t.notes ?? '',
      items: t.items.map((i) => ({
        _uid: nextUid(),
        exercise_id: i.exercise_id,
        position: i.position,
        target_sets: i.target_sets,
        target_reps: i.target_reps,
        target_weight_kg: i.target_weight_kg,
        rest_seconds: i.rest_seconds,
      })),
    })
    setOpen(true)
  }

  const updateItem = (index: number, patch: Partial<TemplateItemInput>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
    }))
  }

  const removeItem = (index: number) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }))
  }

  const changeExercise = (index: number, nextId: number) => {
    const next = exercises.find((ex) => ex.id === nextId)
    const stats = latestStats.get(nextId)
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => {
        if (i !== index) return it
        if (stats) return { ...it, exercise_id: nextId, ...valuesFromLatest(stats, next) }
        // 記録の無い種目は値をそのまま残す。休憩だけは種目ごとに適正が違うので新しい種目の既定値に入れ替える
        return { ...it, exercise_id: nextId, rest_seconds: next?.default_rest_seconds ?? it.rest_seconds }
      }),
    }))
  }

  const applyLatestTo = (index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => {
        const stats = latestStats.get(it.exercise_id)
        if (i !== index || !stats) return it
        const exercise = exercises.find((ex) => ex.id === it.exercise_id)
        return { ...it, ...valuesFromLatest(stats, exercise) }
      }),
    }))
  }

  const applyLatestToAll = () => {
    const applied = form.items.filter((it) => latestStats.has(it.exercise_id)).length
    if (applied === 0) {
      toast.info('記録のある種目がありません')
      return
    }
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it) => {
        const stats = latestStats.get(it.exercise_id)
        if (!stats) return it
        const exercise = exercises.find((ex) => ex.id === it.exercise_id)
        return { ...it, ...valuesFromLatest(stats, exercise) }
      }),
    }))
    toast.success(`${applied}件の種目に直近の記録を反映しました`)
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setForm((prev) => {
      const oldIndex = prev.items.findIndex((it) => it._uid === active.id)
      const newIndex = prev.items.findIndex((it) => it._uid === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      const reordered = arrayMove(prev.items, oldIndex, newIndex)
      return { ...prev, items: reordered.map((it, i) => ({ ...it, position: i })) }
    })
  }

  const addItem = () => {
    const first = exercises[0]
    if (!first) {
      toast.error('先に種目を登録してください')
      return
    }
    const stats = latestStats.get(first.id)
    const values: ItemValues = stats
      ? valuesFromLatest(stats, first)
      : {
          target_sets: DEFAULT_TARGET_SETS,
          target_reps: DEFAULT_TARGET_REPS,
          target_weight_kg: null,
          rest_seconds: first.default_rest_seconds,
        }
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { _uid: nextUid(), exercise_id: first.id, position: prev.items.length, ...values }],
    }))
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('テンプレート名を入力してください')
      return
    }
    try {
      const itemsPayload = form.items.map(({ _uid: _, ...rest }) => ({
        ...rest,
        target_sets: rest.target_sets ?? DEFAULT_TARGET_SETS,
        target_reps: rest.target_reps ?? DEFAULT_TARGET_REPS,
      }))
      if (editing) {
        await updateTemplate(editing.id, { name: form.name, notes: form.notes, items: itemsPayload })
      } else {
        await createTemplate({ name: form.name, notes: form.notes, items: itemsPayload })
      }
      setOpen(false)
      await reload()
    } catch (err) {
      console.error(err)
      toast.error('保存に失敗しました')
    }
  }

  const handleArchive = async (t: WorkoutTemplate) => {
    if (!(await confirmDialog({ title: `「${t.name}」を削除しますか？`, destructive: true }))) return
    try {
      await archiveTemplate(t.id)
      await reload()
    } catch (err) {
      console.error(err)
      toast.error('削除に失敗しました')
    }
  }

  const handleAiPropose = async () => {
    setAiLoading(true)
    setAiProposals([])
    try {
      const res = await proposeTemplates({ goal: aiGoal, user_request: aiRequest || null })
      setAiProposals(res.proposals)
    } catch (err) {
      console.error(err)
      toast.error('AI提案の取得に失敗しました')
    } finally {
      setAiLoading(false)
    }
  }

  const handleAdoptProposal = async (proposal: AiTemplateProposal) => {
    // exercise_name → exercise_id のマッピング
    const nameToId = new Map(exercises.map((e) => [e.name, e.id]))
    const items: TemplateItemInput[] = proposal.items
      .filter((it) => nameToId.has(it.exercise_name))
      .map((it, i) => ({
        exercise_id: nameToId.get(it.exercise_name)!,
        position: i,
        target_sets: it.target_sets,
        target_reps: it.target_reps,
        rest_seconds: it.rest_seconds,
      }))

    try {
      await createTemplate({ name: proposal.template_name, notes: proposal.description, items })
      setAiOpen(false)
      setAiProposals([])
      toast.success(`「${proposal.template_name}」を作成しました`)
      await reload()
    } catch (err) {
      console.error(err)
      toast.error('テンプレート作成に失敗しました')
    }
  }

  return (
    <div className="flex flex-col h-screen">
      <Header title="テンプレート" onClose={onClose} />
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        <div className="flex gap-2">
          <Button onClick={openNew} className="flex-1">
            <Plus className="size-4 mr-1" /> 新規テンプレート
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const profile = await getProfile()
                if (isProfileIncomplete(profile)) {
                  setProfileGuideOpen(true)
                  return
                }
              } catch {
                setProfileGuideOpen(true)
                return
              }
              setAiProposals([])
              setAiGoal('リーンバルク')
              setAiRequest('')
              setAiOpen(true)
            }}
          >
            <Sparkles className="size-4 mr-1" /> AIで作成
          </Button>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">読み込み中...</div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">テンプレートがありません</div>
        ) : (
          templates.map((t) => (
            <Card key={t.id} className="p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.items.length} 種目</div>
                  <ul className="text-xs mt-1 space-y-0.5">
                    {t.items.map((i) => (
                      <li key={i.id}>
                        ・{i.exercise_name} {i.target_sets}×{i.target_reps}
                        {i.target_weight_kg ? ` @ ${i.target_weight_kg}kg` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(t)}>
                  <Pencil className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleArchive(t)}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-x-hidden overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'テンプレート編集' : '新規テンプレート'}</DialogTitle>
          </DialogHeader>
          <div className="min-w-0 space-y-3">
            <div>
              <Label>名前</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>メモ</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>種目</Label>
                <div className="flex gap-2">
                  {form.items.some((it) => latestStats.has(it.exercise_id)) && (
                    <Button size="sm" variant="outline" onClick={applyLatestToAll}>
                      <History className="size-4 mr-1" /> 直近の記録を反映
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={addItem}>
                    <Plus className="size-4 mr-1" /> 種目追加
                  </Button>
                </div>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={form.items.map((it) => it._uid)} strategy={verticalListSortingStrategy}>
                  {form.items.map((item, index) => (
                    <SortableExerciseItem
                      key={item._uid}
                      id={item._uid}
                      item={item}
                      index={index}
                      exercises={exercises}
                      latest={latestStats.get(item.exercise_id)}
                      onUpdate={updateItem}
                      onChangeExercise={changeExercise}
                      onApplyLatest={applyLatestTo}
                      onRemove={removeItem}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSubmit}>{editing ? '保存' : '作成'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* プロフィール未設定案内ダイアログ */}
      <Dialog open={profileGuideOpen} onOpenChange={setProfileGuideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>トレーニング設定が必要です</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>AIがあなたに最適なメニューを提案するために、以下の情報が必要です：</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>週のトレーニング頻度（週2回なら全身メニュー、週4回ならPush/Pull分割など）</li>
              <li>利用可能な器具（ジム/自宅で提案できる種目が変わります）</li>
              <li>1セッションの目標時間（時間に合わせて種目数を調整します）</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileGuideOpen(false)}>
              あとで
            </Button>
            <Button
              onClick={() => {
                setProfileGuideOpen(false)
                onOpenProfile()
              }}
            >
              設定画面へ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI提案ダイアログ */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-h-[90dvh] overflow-x-hidden overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Sparkles className="size-4 inline mr-1" /> AIテンプレート提案
            </DialogTitle>
          </DialogHeader>

          {aiProposals.length === 0 ? (
            <div className="space-y-3">
              <div>
                <Label>目的</Label>
                <Select value={aiGoal} onValueChange={setAiGoal}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="減量">減量（脂肪燃焼）</SelectItem>
                    <SelectItem value="リーンバルク">リーンバルク（筋肥大）</SelectItem>
                    <SelectItem value="ダイエット">ダイエット（時短効率）</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>リクエスト（任意）</Label>
                <Textarea
                  value={aiRequest}
                  placeholder="例: 上半身中心のメニューにしてほしい"
                  onChange={(e) => setAiRequest(e.target.value)}
                />
              </div>
              <Button onClick={handleAiPropose} disabled={aiLoading} className="w-full">
                {aiLoading ? '生成中...' : '3つの提案を生成'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {aiProposals.map((proposal, i) => (
                <Card key={i} className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{proposal.coach_name}</Badge>
                    <span className="font-medium text-sm">{proposal.template_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{proposal.description}</p>
                  <ul className="text-xs space-y-0.5">
                    {proposal.items.map((item, j) => (
                      <li key={j}>
                        ・{item.exercise_name} {item.target_sets}×{item.target_reps} (休憩 {item.rest_seconds}s)
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" className="w-full" onClick={() => handleAdoptProposal(proposal)}>
                    この提案を採用
                  </Button>
                </Card>
              ))}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setAiProposals([])
                }}
              >
                やり直す
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

type SortableExerciseItemProps = {
  id: string
  item: FormItem
  index: number
  exercises: Exercise[]
  latest: ExerciseLatestStats | undefined
  onUpdate: (index: number, patch: Partial<TemplateItemInput>) => void
  onChangeExercise: (index: number, exerciseId: number) => void
  onApplyLatest: (index: number) => void
  onRemove: (index: number) => void
}

const formatLatest = (s: ExerciseLatestStats): string => {
  const parts = [`${s.sets}セット`]
  if (s.reps != null) parts.push(`${s.reps}回`)
  if (s.weight_kg != null) parts.push(`${s.weight_kg}kg`)
  return parts.join(' · ')
}

const isLatestApplied = (item: FormItem, latest: ExerciseLatestStats, exercise: Exercise | undefined): boolean => {
  const v = valuesFromLatest(latest, exercise)
  return (
    item.target_sets === v.target_sets &&
    item.target_reps === v.target_reps &&
    item.target_weight_kg === v.target_weight_kg &&
    item.rest_seconds === v.rest_seconds
  )
}

type NumberFieldProps = {
  label: string
  unit?: string
  value: number | null | undefined
  placeholder?: string
  inputMode: 'numeric' | 'decimal'
  step?: string
  onChange: (value: number | null) => void
}

const NumberField = ({ label, unit, value, placeholder, inputMode, step, onChange }: NumberFieldProps) => (
  <div className="min-w-0 space-y-1">
    <Label className="text-xs text-muted-foreground">
      {label}
      {unit && <span className="opacity-70">({unit})</span>}
    </Label>
    <Input
      type="number"
      inputMode={inputMode}
      step={step}
      placeholder={placeholder}
      className="h-10 px-2 text-center text-base tabular-nums"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  </div>
)

const SortableExerciseItem = ({
  id,
  item,
  index,
  exercises,
  latest,
  onUpdate,
  onChangeExercise,
  onApplyLatest,
  onRemove,
}: SortableExerciseItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const applied = latest
    ? isLatestApplied(
        item,
        latest,
        exercises.find((ex) => ex.id === item.exercise_id),
      )
    : false

  return (
    <Card ref={setNodeRef} style={style} className="mb-2 gap-3 p-3">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="並び替え"
          className="-ml-1 cursor-grab touch-none rounded p-1.5 text-muted-foreground hover:bg-accent"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <Select value={String(item.exercise_id)} onValueChange={(v) => onChangeExercise(index, Number(v))}>
          <SelectTrigger className="min-w-0 flex-1 font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {exercises.map((ex) => (
              <SelectItem key={ex.id} value={String(ex.id)}>
                {ex.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" aria-label="種目を削除" onClick={() => onRemove(index)}>
          <X className="size-4" />
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <NumberField
          label="セット"
          inputMode="numeric"
          placeholder={String(DEFAULT_TARGET_SETS)}
          value={item.target_sets}
          onChange={(v) => onUpdate(index, { target_sets: v ?? undefined })}
        />
        <NumberField
          label="レップ"
          inputMode="numeric"
          placeholder={String(DEFAULT_TARGET_REPS)}
          value={item.target_reps}
          onChange={(v) => onUpdate(index, { target_reps: v ?? undefined })}
        />
        <NumberField
          label="重量"
          unit="kg"
          inputMode="decimal"
          step="0.5"
          value={item.target_weight_kg}
          onChange={(v) => onUpdate(index, { target_weight_kg: v })}
        />
        <NumberField
          label="休憩"
          unit="s"
          inputMode="numeric"
          value={item.rest_seconds}
          onChange={(v) => onUpdate(index, { rest_seconds: v })}
        />
      </div>
      {latest && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 py-1 pr-1 pl-2.5 text-xs">
          <History className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="shrink-0 text-muted-foreground">{format(parseUtcDate(latest.date), 'M/d')}</span>
          <span className="min-w-0 flex-1 truncate font-medium tabular-nums">{formatLatest(latest)}</span>
          {applied ? (
            <span className="flex shrink-0 items-center gap-1 px-2 text-muted-foreground">
              <Check className="size-3.5" /> 反映済み
            </span>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 px-2.5"
              onClick={() => onApplyLatest(index)}
            >
              反映
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}
